import { useState } from 'react';
import { Check, Zap, BarChart, Globe, CreditCard, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { auth } from '../lib/api';
import PromoCodeInput from '../components/PromoCodeInput';


// Credit bundles - optimized for payment processing fees
interface CreditBundle {
  name: string;
  nameFil: string;
  price: number;
  basicCredits: number;
  enhancedCredits: number;
  bonusPercent: number;
  popular?: boolean;
  bestValue?: boolean;
  forIndividuals?: boolean;
}

const creditBundles: CreditBundle[] = [
  {
    name: 'Personal',
    nameFil: 'Personal',
    price: 100,
    basicCredits: 4,
    enhancedCredits: 2,
    bonusPercent: 0,
    forIndividuals: true,
  },
  {
    name: 'Starter',
    nameFil: 'Starter',
    price: 500,
    basicCredits: 20,
    enhancedCredits: 11,
    bonusPercent: 0,
  },
  {
    name: 'Business',
    nameFil: 'Business',
    price: 2500,
    basicCredits: 120,
    enhancedCredits: 66,
    bonusPercent: 20,
    popular: true,
  },
  {
    name: 'Enterprise',
    nameFil: 'Enterprise',
    price: 5000,
    basicCredits: 260,
    enhancedCredits: 143,
    bonusPercent: 30,
    bestValue: true,
  },
];

interface ProductPlan {
  name: string;
  nameFil: string;
  description: string;
  descriptionFil: string;
  priceField: 'basic' | 'enhanced';
  features: string[];
  featuresFil: string[];
  popular?: boolean;
}

const verificationTypes: ProductPlan[] = [
  {
    name: 'Basic Verification',
    nameFil: 'Basic na Verification',
    description: 'Document + Liveness + Cryptographic Audit Trail',
    descriptionFil: 'Dokumento + Liveness + Cryptographic Audit Trail',
    priceField: 'basic',
    features: [
      'Face Match (Selfie vs ID)',
      'Liveness detection (blink, movement)',
      'All Philippine IDs supported',
      'Instant results',
    ],
    featuresFil: [
      'Face Match (Selfie vs ID)',
      'Liveness detection (kurap, galaw)',
      'Lahat ng Philippine IDs',
      'Instant na resulta',
    ],
  },
  {
    name: 'Enhanced Verification',
    nameFil: 'Enhanced na Verification',
    description: 'Basic + PhilSys QR Digital Authenticity Check',
    descriptionFil: 'Basic + PhilSys QR Digital Authenticity Check',
    priceField: 'enhanced',
    popular: true,
    features: [
      'Everything in Basic',
      'AI OCR Text Extraction',
      'Anti-Forge QR Matching',
      'Data-to-QR Cross Validation',
      'Government Database (Coming Soon)',
    ],
    featuresFil: [
      'Lahat sa Basic',
      'AI OCR Text Extraction',
      'Anti-Forge QR Matching',
      'Data-to-QR Cross Validation',
      'Gov Database (Malapit na)',
    ],
  },
];

// Payment methods available
const paymentMethods = [
  { name: 'Bank QR / InstaPay', icon: '🏦', available: true, comingSoon: false },
  { name: 'GCash', icon: '💚', available: false, comingSoon: true },
  { name: 'Maya', icon: '💜', available: false, comingSoon: true },
];

export default function Pricing() {
  const [language, setLanguage] = useState<'en' | 'fil'>('en');
  const [showPerVerification, setShowPerVerification] = useState(false);

  const getText = (en: string, fil: string) => language === 'en' ? en : fil;

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getEffectivePrice = (bundle: CreditBundle, type: 'basic' | 'enhanced') => {
    const credits = type === 'basic' ? bundle.basicCredits : bundle.enhancedCredits;
    return (bundle.price / credits).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gray-900 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            {getText('Simple, Transparent Pricing', 'Simple at Transparent na Presyo')}
          </h1>
          <p className="text-xl text-gray-300">
            {getText(
              'Buy credits in bundles and save up to 30%. No hidden fees.',
              'Bumili ng credits sa bundles at makatipid ng hanggang 30%. Walang hidden fees.'
            )}
          </p>
        </div>

        {/* Language Toggle */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setLanguage('en')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${language === 'en'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white'
                }`}
            >
              English
            </button>
            <button
              onClick={() => setLanguage('fil')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${language === 'fil'
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white'
                }`}
            >
              Filipino
            </button>
          </div>
        </div>

        {/* Promo Code Input */}
        <div className="max-w-2xl mx-auto mb-12">
          <PromoCodeInput onSuccess={() => {
            // Optionally refresh balance or show success message
            window.location.reload();
          }} />
        </div>

        {/* Credit Bundles */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <CreditCard className="h-4 w-4" />
              {getText('Recommended', 'Inirerekomenda')}
            </div>
            <h2 className="text-2xl font-bold text-white">
              {getText('Credit Bundles', 'Credit Bundles')}
            </h2>
            <p className="text-gray-400 mt-2">
              {getText(
                'One payment, multiple verifications. Best value for businesses.',
                'Isang bayad, maraming verification. Pinakamahusay para sa negosyo.'
              )}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {creditBundles.map((bundle, index) => (
              <motion.div
                key={bundle.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative glass-card rounded-2xl overflow-hidden ${
                  bundle.popular ? 'ring-2 ring-blue-500' : ''
                } ${bundle.bestValue ? 'ring-2 ring-green-500' : ''}`}
              >
                {bundle.popular && (
                  <div className="absolute top-0 right-0 bg-blue-600 text-white px-3 py-1 text-xs font-medium rounded-bl-lg">
                    {getText('Popular', 'Sikat')}
                  </div>
                )}
                {bundle.bestValue && (
                  <div className="absolute top-0 right-0 bg-green-600 text-white px-3 py-1 text-xs font-medium rounded-bl-lg flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    {getText('Best Value', 'Pinakamura')}
                  </div>
                )}

                <div className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-1">
                    {getText(bundle.name, bundle.nameFil)}
                  </h3>

                  {bundle.bonusPercent > 0 && (
                    <span className="inline-block bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full mb-3">
                      +{bundle.bonusPercent}% {getText('bonus credits', 'bonus credits')}
                    </span>
                  )}

                  <div className="flex items-baseline mb-4">
                    <span className="text-3xl font-bold text-white">
                      {formatPrice(bundle.price)}
                    </span>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-400">Basic Credits</span>
                      <span className="text-white font-medium">{bundle.basicCredits}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-400">Enhanced Credits</span>
                      <span className="text-white font-medium">{bundle.enhancedCredits}</span>
                    </div>
                    <div className="border-t border-gray-700 pt-3">
                      <div className="flex justify-between items-center text-xs text-gray-500">
                        <span>{getText('Effective price (Basic)', 'Effective price (Basic)')}</span>
                        <span>~₱{getEffectivePrice(bundle, 'basic')}/ea</span>
                      </div>
                      <div className="flex justify-between items-center text-xs text-gray-500 mt-1">
                        <span>{getText('Effective price (Enhanced)', 'Effective price (Enhanced)')}</span>
                        <span>~₱{getEffectivePrice(bundle, 'enhanced')}/ea</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      if (auth.isAuthenticated()) {
                        window.location.href = '/dashboard?purchase=' + bundle.name.toLowerCase();
                      } else {
                        window.location.href = '/register';
                      }
                    }}
                    className={`block w-full text-center py-2.5 px-4 rounded-lg font-medium transition-colors ${
                      bundle.popular || bundle.bestValue
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                  >
                    {getText('Buy Credits', 'Bumili ng Credits')}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Verification Types */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white">
              {getText('Verification Types', 'Uri ng Verification')}
            </h2>
            <p className="text-gray-400 mt-2">
              {getText(
                'Choose the right verification level for your needs',
                'Piliin ang tamang antas ng verification para sa iyong pangangailangan'
              )}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {verificationTypes.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative glass-card rounded-2xl overflow-hidden
                  ${plan.popular ? 'ring-2 ring-blue-500' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-blue-600 text-white px-4 py-1 text-sm font-medium rounded-bl-lg">
                    {getText('Popular', 'Sikat')}
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {getText(plan.name, plan.nameFil)}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    {getText(plan.description, plan.descriptionFil)}
                  </p>

                  <div className="flex items-baseline mb-6">
                    <span className="text-3xl font-bold text-white">
                      1 {plan.priceField === 'basic' ? 'Basic' : 'Enhanced'}
                    </span>
                    <span className="ml-2 text-gray-400">
                      {getText('credit per verification', 'credit bawat verification')}
                    </span>
                  </div>

                  <ul className="space-y-3">
                    {(language === 'en' ? plan.features : plan.featuresFil).map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Per-verification pricing (collapsible) */}
        <div className="mb-16">
          <button
            onClick={() => setShowPerVerification(!showPerVerification)}
            className="mx-auto block text-blue-400 hover:text-blue-300 mb-4"
          >
            {showPerVerification
              ? getText('Hide per-verification pricing', 'Itago ang per-verification pricing')
              : getText('View per-verification pricing (without bundles)', 'Tingnan ang per-verification pricing (walang bundles)')}
          </button>

          {showPerVerification && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="max-w-3xl mx-auto glass-card rounded-2xl p-8"
            >
              <div className="text-center mb-4">
                <span className="inline-block bg-yellow-500/20 text-yellow-400 text-xs px-3 py-1 rounded-full">
                  {getText('Higher fees apply', 'Mas mataas ang fees')}
                </span>
              </div>
              <p className="text-gray-400 text-center mb-6 text-sm">
                {getText(
                  'Individual verifications incur payment processing fees. Bundles are more cost-effective.',
                  'Ang individual verifications ay may payment processing fees. Mas mura ang bundles.'
                )}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-400">
                        {getText('Verification Type', 'Uri ng Verification')}
                      </th>
                      <th className="text-left py-3 px-4 text-gray-400">
                        {getText('Price', 'Presyo')}
                      </th>
                      <th className="text-left py-3 px-4 text-gray-400">
                        {getText('Bundle Price', 'Presyo sa Bundle')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-800">
                      <td className="py-3 px-4 text-white">Basic</td>
                      <td className="py-3 px-4 text-white">₱25</td>
                      <td className="py-3 px-4 text-green-400">~₱19 (Enterprise)</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-3 px-4 text-white">Enhanced</td>
                      <td className="py-3 px-4 text-white">₱45</td>
                      <td className="py-3 px-4 text-green-400">~₱35 (Enterprise)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </div>

        {/* Payment Methods */}
        <div className="max-w-4xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-center text-white mb-8">
            {getText('Accepted Payment Methods', 'Mga Tinatanggap na Paraan ng Pagbabayad')}
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {paymentMethods.map((method) => (
              <div
                key={method.name}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 ${
                  method.comingSoon
                    ? 'bg-gray-800/50 opacity-70'
                    : 'bg-gray-800'
                }`}
              >
                <span className={`text-2xl ${method.comingSoon ? 'grayscale' : ''}`}>
                  {method.icon}
                </span>
                <span className={method.comingSoon ? 'text-gray-400' : 'text-white'}>
                  {method.name}
                </span>
                {method.comingSoon && (
                  <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">
                    Soon
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-white mb-12">
            {getText('Enterprise-Grade Features', 'Enterprise-Grade Features')}
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <img
                    src="/Agos_logo.png"
                    alt="Agos Logo"
                    className="h-12 w-12 object-contain"
                  />
                  <div className="absolute inset-0 bg-blue-500/20 blur-lg" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {getText('Maximum Security', 'Maximum na Seguridad')}
              </h3>
              <p className="text-gray-300">
                {getText(
                  'Engineered for BSP eKYC Compliance',
                  'Dinisenyo para sa BSP eKYC Compliance'
                )}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-center"
            >
              <Zap className="h-12 w-12 text-blue-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {getText('Lightning Fast', 'Napakabilis')}
              </h3>
              <p className="text-gray-300">
                {getText(
                  'Instant verification with cryptographic audit trail',
                  'Instant verification na may cryptographic audit trail'
                )}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center"
            >
              <BarChart className="h-12 w-12 text-blue-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {getText('Advanced Analytics', 'Advanced Analytics')}
              </h3>
              <p className="text-gray-300">
                {getText(
                  'Comprehensive dashboard with real-time insights',
                  'Comprehensive dashboard na may real-time insights'
                )}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center"
            >
              <Globe className="h-12 w-12 text-blue-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                {getText('NPC Compliant', 'NPC Compliant')}
              </h3>
              <p className="text-gray-300">
                {getText(
                  'Designed for Philippine Data Privacy Act compliance',
                  'Dinisenyo para sa Philippine Data Privacy Act compliance'
                )}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Philippine IDs Supported */}
        <div className="max-w-4xl mx-auto mt-16">
          <h2 className="text-2xl font-bold text-center text-white mb-8">
            {getText('Supported Philippine IDs', 'Mga Suportadong Philippine IDs')}
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'PhilID (National ID)',
              'UMID',
              "Driver's License",
              'Passport',
              'Postal ID',
              'PhilHealth ID',
              'TIN Card',
              "Voter's ID",
              'PRC License',
            ].map((id) => (
              <span
                key={id}
                className="bg-gray-800 text-gray-300 px-3 py-1 rounded-full text-sm"
              >
                {id}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
